import { Table, TableBody, TableHead } from "@mui/material";
import React from "react";

import {
  HeaderTableCell,
  TableContainer,
  TableHeaderRow,
} from "@/components/molecules/Table/styled";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import {
  isWorkflowStepInUse,
  WORKFLOW_STEP_TABLE_COLUMNS,
} from "@/components/screens/WorkflowFormPage/constants";
import usePopOver from "@/hooks/use-popover";
import { IWorkflowStep } from "@/interfaces/workflow-step.interface";
import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";

import { WorkflowStepTableRow } from "./row";
import { WorkflowStepPopover } from "../StepPopover";

interface WorkflowStepTableProps {
  workflowSteps: IWorkflowStep[] | undefined;
  messages: WorkflowEditDialogMessages;
  handleOpenDialog: (isEditing: boolean, step?: IWorkflowStep) => void;
  setWorkflowStep: React.Dispatch<React.SetStateAction<IWorkflowStep | null>>;
  isAdmin: boolean;
  onRemove: (step: IWorkflowStep) => void;
  isLoadingPopover: boolean;
  noStepsMessage: string;
}

export function WorkflowStepTable({
  workflowSteps,
  messages,
  handleOpenDialog,
  setWorkflowStep,
  isAdmin,
  onRemove,
  isLoadingPopover,
  noStepsMessage,
}: WorkflowStepTableProps) {
  const columns = WORKFLOW_STEP_TABLE_COLUMNS(messages);

  const {
    openPopover,
    anchorEl,
    closePopover,
    isOpen,
    selected: selectedStep,
  } = usePopOver<IWorkflowStep>();

  const hasSteps = workflowSteps && workflowSteps.length > 0;
  const sortedSteps = hasSteps
    ? [...workflowSteps].sort((a, b) => (a.stepNumber ?? 0) - (b.stepNumber ?? 0))
    : [];

  return (
    <>
      {hasSteps ? (
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableHeaderRow>
                {columns.map((label, index) => (
                  <HeaderTableCell key={`${index} - ${label}`} width={"16%"}>
                    {label}
                  </HeaderTableCell>
                ))}
              </TableHeaderRow>
            </TableHead>
            <TableBody>
              {sortedSteps.map((singleStep, index) => (
                <WorkflowStepTableRow
                  key={`${index} - ${singleStep.id}`}
                  step={singleStep}
                  inUse={isWorkflowStepInUse(singleStep, sortedSteps)}
                  onOpenPopover={openPopover}
                  messages={messages}
                  isAdmin={isAdmin}
                  onRemove={onRemove}
                  isLoadingPopover={isLoadingPopover}
                />
              ))}
            </TableBody>
          </Table>
          {selectedStep && (
            <WorkflowStepPopover
              isOpen={isOpen}
              anchorEl={anchorEl}
              onClose={closePopover}
              messages={messages}
              selectedWorkflowStep={selectedStep}
              handleOpenDialog={handleOpenDialog}
              setWorkflowStep={setWorkflowStep}
              isAdmin={isAdmin}
              onRemove={onRemove}
              isLoading={isLoadingPopover}
            />
          )}
        </TableContainer>
      ) : (
        <MDBox py={3} display="flex" justifyContent="center">
          <MDTypography variant="body2" color="text">
            {noStepsMessage}
          </MDTypography>
        </MDBox>
      )}
    </>
  );
}
